import { useState, useEffect, useMemo } from 'react';
import { useSnapshot } from 'valtio';
import { state } from '../store/global';
import {
  getSnapshots,
  getSnapshot,
  getBars,
  getNews,
  getOptionContracts,
  getMostActives,
  searchAssets,
  periodToTimeframe,
  placeStockOrder,
  placeOptionOrder,
  getAccount,
  getPositions,
  getOrders,
  getAlpacaAccountId,
  calculateAlpacaCommission,
  ALPACA_MIN_TRADE_USD,
} from '../lib/alpaca';
import InvestorOnboarding, { PORTFOLIOS } from '../components/InvestorOnboarding';
import RoboAdvisor from '../components/RoboAdvisor';
import NewsFeed from '../components/NewsFeed';
import StockAnalysis from '../components/StockAnalysis';
import PortfolioAnalysis from '../components/PortfolioAnalysis';
import StockComparator from '../components/StockComparator';
import HeroAudioPlayer from '../components/HeroAudioPlayer';
import TickerPopover from '../components/TickerPopover';
import { getPortfolioAudioUrl } from '../lib/oraculoAudio';
import { getDemoTier, getAvailableSymbols } from '../lib/cerebro';
import { getTickerInfo } from '../lib/tickerInfo';
import { trackStockView, getViewedCount } from '../components/Achievements';

const POPULAR_SYMBOLS = [
  'AAPL', 'MSFT', 'NVDA', 'TSLA', 'SPCX', 'CAI', 'AMZN', 'GOOGL', 'META', 'NFLX',
  'PLTR', 'HIMS', 'DUOL', 'AMD', 'COIN', 'SNOW', 'UBER', 'SQ',
  'SHOP', 'CRM', 'PYPL', 'DIS', 'BA', 'JPM', 'V', 'MA',
];
const ETF_SYMBOLS = [
  'VOO', 'QQQ', 'VTI', 'SPY', 'IWM', 'DIA',
  'GLD', 'SLV', 'TLT', 'VWO', 'EEM', 'ARKK',
  'XLF', 'XLE', 'XLK', 'SOXX', 'VNQ', 'BND',
];

const STOCK_NAMES = {
  AAPL: 'Apple Inc.', MSFT: 'Microsoft Corporation', NVDA: 'NVIDIA Corporation',
  RXRX: 'Recursion Pharmaceuticals', TEM: 'Tempus AI', NAUT: 'Nautilus Biotechnology', IBRX: 'ImmunityBio', INKT: 'MiNK Therapeutics',
  TSLA: 'Tesla Inc.', SPCX: 'SpaceX', CAI: 'Caris Life Sciences', AMZN: 'Amazon.com Inc.', GOOGL: 'Alphabet Inc.', META: 'Meta Platforms Inc.',
  NFLX: 'Netflix Inc.', PLTR: 'Palantir Technologies', HIMS: 'Hims & Hers Health',
  DUOL: 'Duolingo Inc.', AMD: 'Advanced Micro Devices', COIN: 'Coinbase Global',
  SNOW: 'Snowflake Inc.', UBER: 'Uber Technologies', SQ: 'Block Inc. (Square)',
  SHOP: 'Shopify Inc.', CRM: 'Salesforce Inc.', PYPL: 'PayPal Holdings',
  DIS: 'Walt Disney Company', BA: 'Boeing Company', JPM: 'JPMorgan Chase',
  V: 'Visa Inc.', MA: 'Mastercard Inc.', WMT: 'Walmart Inc.',
  KO: 'Coca-Cola Company', PEP: 'PepsiCo Inc.', NKE: 'Nike Inc.',
  INTC: 'Intel Corporation', ORCL: 'Oracle Corporation', ADBE: 'Adobe Inc.',
  CSCO: 'Cisco Systems', QCOM: 'Qualcomm Inc.', TXN: 'Texas Instruments',
  AVGO: 'Broadcom Inc.', COST: 'Costco Wholesale', HD: 'Home Depot',
  MCD: 'McDonald\'s Corp.', SBUX: 'Starbucks Corp.', PG: 'Procter & Gamble',
  JNJ: 'Johnson & Johnson', UNH: 'UnitedHealth Group', LLY: 'Eli Lilly',
  PFE: 'Pfizer Inc.', ABBV: 'AbbVie Inc.', MRK: 'Merck & Co.',
  XOM: 'Exxon Mobil', CVX: 'Chevron Corporation', GS: 'Goldman Sachs',
  MS: 'Morgan Stanley', BAC: 'Bank of America', WFC: 'Wells Fargo',
  C: 'Citigroup Inc.', 'BRK.B': 'Berkshire Hathaway', T: 'AT&T Inc.',
  VZ: 'Verizon Communications', TMUS: 'T-Mobile US', ABNB: 'Airbnb Inc.',
  SNAP: 'Snap Inc.', PINS: 'Pinterest Inc.', SPOT: 'Spotify Technology',
  ZM: 'Zoom Video', ROKU: 'Roku Inc.', RBLX: 'Roblox Corp.',
  U: 'Unity Software', DKNG: 'DraftKings Inc.', RIVN: 'Rivian Automotive',
  LCID: 'Lucid Group', NIO: 'NIO Inc.', LI: 'Li Auto Inc.',
  SOFI: 'SoFi Technologies', HOOD: 'Robinhood Markets', MELI: 'MercadoLibre',
  NU: 'Nu Holdings', SE: 'Sea Limited', GRAB: 'Grab Holdings',
  BABA: 'Alibaba Group', JD: 'JD.com Inc.', PDD: 'PDD Holdings (Temu)',
  TSM: 'Taiwan Semiconductor', ASML: 'ASML Holding', ARM: 'Arm Holdings',
  SMCI: 'Super Micro Computer', DELL: 'Dell Technologies', PANW: 'Palo Alto Networks',
  CRWD: 'CrowdStrike Holdings', NET: 'Cloudflare Inc.', DDOG: 'Datadog Inc.',
  MDB: 'MongoDB Inc.', TEAM: 'Atlassian Corp.', NOW: 'ServiceNow Inc.',
  WDAY: 'Workday Inc.', ZS: 'Zscaler Inc.', OKTA: 'Okta Inc.',
  TTD: 'The Trade Desk', CPNG: 'Coupang Inc.', AFRM: 'Affirm Holdings',
  PATH: 'UiPath Inc.', AI: 'C3.ai Inc.', IONQ: 'IonQ Inc.',
  RGTI: 'Rigetti Computing', LUNR: 'Intuitive Machines', RKLB: 'Rocket Lab',
  VOO: 'Vanguard S&P 500 ETF', QQQ: 'Invesco QQQ Trust', VTI: 'Vanguard Total Stock',
  SPY: 'SPDR S&P 500 ETF', IWM: 'iShares Russell 2000', DIA: 'SPDR Dow Jones ETF',
  GLD: 'SPDR Gold Shares', SLV: 'iShares Silver Trust', TLT: 'iShares 20+ Year Treasury',
  VWO: 'Vanguard FTSE Emerging', EEM: 'iShares MSCI Emerging', ARKK: 'ARK Innovation ETF',
  XLF: 'Financial Select SPDR', XLE: 'Energy Select SPDR', XLK: 'Technology Select SPDR',
  SOXX: 'iShares Semiconductor', VNQ: 'Vanguard Real Estate', BND: 'Vanguard Total Bond',
};

const formatPrice = (n) => `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const formatPct = (n) => `${n >= 0 ? '+' : ''}${Number(n).toFixed(2)}%`;

function StockRow({ symbol, snap, onClick }) {
  if (!snap) return null;
  const price = snap.latestTrade?.p || snap.dailyBar?.c || 0;
  const prevClose = snap.prevDailyBar?.c || 0;
  const change = price - prevClose;
  const pct = prevClose ? (change / prevClose) * 100 : 0;
  const isPos = pct >= 0;
  return (
    <div
      onClick={() => onClick(symbol)}
      className="flex items-center px-5 py-3.5 border-b border-[#F5F5F5] last:border-b-0 cursor-pointer hover:bg-[#FAFAFA] transition"
    >
      <div className="w-10 h-10 rounded-lg bg-[#F5F5F5] flex items-center justify-center mr-3 flex-shrink-0 text-sm font-bold text-[#2A59C6]">
        {symbol.slice(0, 2)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-bold text-[#2A59C6]">{symbol}</div>
        <div className="text-xs text-[#6B7280] truncate">{STOCK_NAMES[symbol] || ''}</div>
      </div>
      <div className="text-right min-w-[100px]">
        <div className="text-sm font-bold text-[#181D27]">{formatPrice(price)}</div>
        <div className={`text-xs font-semibold ${isPos ? 'text-green-600' : 'text-red-600'}`}>
          {formatPct(pct)}
        </div>
      </div>
    </div>
  );
}

function StockList({ symbols, snapshots, onSelect }) {
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden">
      {symbols.map((s) => (
        <StockRow key={s} symbol={s} snap={snapshots[s]} onClick={onSelect} />
      ))}
    </div>
  );
}

function MiniChart({ bars, isPos }) {
  if (!bars || bars.length === 0) {
    return <div className="h-64 flex items-center justify-center text-[#9CA3AF] text-xs">Sin datos</div>;
  }
  const prices = bars.map((b) => b.c);
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  const range = max - min || 1;
  const pad = range * 0.08;
  const yMin = min - pad;
  const yMax = max + pad;
  const yRange = yMax - yMin;
  const w = 700, h = 240, ml = 65, mr = 15, mt = 10, mb = 25;
  const cw = w - ml - mr, ch = h - mt - mb;
  const color = isPos ? '#16a34a' : '#dc2626';
  const bgColor = isPos ? 'rgba(22,163,74,0.08)' : 'rgba(220,38,38,0.08)';

  // Y-axis ticks (5 levels)
  const yTicks = [];
  for (let i = 0; i <= 4; i++) {
    const val = yMin + (yRange * i) / 4;
    const y = mt + ch - (ch * i) / 4;
    yTicks.push({ val, y });
  }

  // X-axis labels (5 dates)
  const xLabels = [];
  const step = Math.max(1, Math.floor(bars.length / 4));
  for (let i = 0; i < bars.length; i += step) {
    const x = ml + (i / (prices.length - 1)) * cw;
    const d = new Date(bars[i].t);
    const label = d.toLocaleDateString('es', { day: '2-digit', month: 'short' });
    xLabels.push({ x, label });
  }

  const points = prices.map((p, i) => {
    const x = ml + (i / (prices.length - 1)) * cw;
    const y = mt + ch - ((p - yMin) / yRange) * ch;
    return `${x},${y}`;
  });
  const line = points.join(' ');
  const firstX = ml;
  const lastX = ml + cw;
  const fillPoints = `${firstX},${mt + ch} ${line} ${lastX},${mt + ch}`;

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-64">
      {/* Grid lines */}
      {yTicks.map((t, i) => (
        <line key={i} x1={ml} y1={t.y} x2={w - mr} y2={t.y} stroke="#F0F0F0" strokeWidth="1" />
      ))}
      {/* Y-axis labels */}
      {yTicks.map((t, i) => (
        <text key={i} x={ml - 8} y={t.y + 4} textAnchor="end" fontSize="11" fill="#9CA3AF" fontFamily="system-ui">
          ${t.val.toFixed(2)}
        </text>
      ))}
      {/* X-axis labels */}
      {xLabels.map((t, i) => (
        <text key={i} x={t.x} y={h - 5} textAnchor="middle" fontSize="10" fill="#9CA3AF" fontFamily="system-ui">
          {t.label}
        </text>
      ))}
      {/* Fill area */}
      <polygon points={fillPoints} fill={bgColor} />
      {/* Line */}
      <polyline points={line} fill="none" stroke={color} strokeWidth="2.5" strokeLinejoin="round" />
      {/* Start and end dots */}
      {points.length > 0 && (
        <>
          <circle cx={points[0].split(',')[0]} cy={points[0].split(',')[1]} r="3" fill={color} />
          <circle cx={points[points.length-1].split(',')[0]} cy={points[points.length-1].split(',')[1]} r="4" fill={color} stroke="white" strokeWidth="2" />
        </>
      )}
    </svg>
  );
}

function StockDetail({ symbol, onBack, accountId }) {
  const [snap, setSnap] = useState(null);
  const [bars, setBars] = useState([]);
  const [news, setNews] = useState([]);
  const [period, setPeriod] = useState('1M');
  const [tab, setTab] = useState('accion');
  const [optType, setOptType] = useState('call');
  const [contracts, setContracts] = useState([]);
  const [allContracts, setAllContracts] = useState([]);
  const [expDates, setExpDates] = useState([]);
  const [selectedExp, setSelectedExp] = useState('');
  const [selectedOption, setSelectedOption] = useState(null);
  const [optMode, setOptMode] = useState('buy');
  const [optQty, setOptQty] = useState('1');
  const [buyAmt, setBuyAmt] = useState('');
  const [buyMode, setBuyMode] = useState('buy');
  const [orderLoading, setOrderLoading] = useState(false);
  const [orderResult, setOrderResult] = useState(null);
  const [orderError, setOrderError] = useState(null);
  const [optOrderLoading, setOptOrderLoading] = useState(false);
  const [optOrderResult, setOptOrderResult] = useState(null);
  const [optOrderError, setOptOrderError] = useState(null);
  const [newsError, setNewsError] = useState(false);

  useEffect(() => {
    trackStockView(symbol);
    let cancelled = false;
    const fetchSnapshot = async () => {
      try {
        const snaps = await getSnapshots([symbol]);
        if (!cancelled) setSnap(snaps[symbol]);
      } catch (e) { console.error('snapshot:', e); }
    };
    fetchSnapshot();
    const interval = setInterval(fetchSnapshot, 30000);
    (async () => {
      try {
        const n = await getNews([symbol], 8);
        if (!cancelled) setNews(n);
      } catch (e) { console.error('news:', e); if (!cancelled) setNewsError(true); }
    })();
    return () => { cancelled = true; clearInterval(interval); };
  }, [symbol]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { timeframe, days } = periodToTimeframe(period);
        const start = new Date(Date.now() - days * 86400000).toISOString();
        const b = await getBars(symbol, timeframe, start);
        if (!cancelled) setBars(b);
      } catch (e) { console.error('bars:', e); }
    })();
    return () => { cancelled = true; };
  }, [symbol, period]);

  useEffect(() => {
    if (tab !== 'opciones') return;
    let cancelled = false;
    (async () => {
      try {
        const c = await getOptionContracts(symbol, optType);
        if (!cancelled) {
          setAllContracts(c);
          // Extract unique expiration dates
          const dates = [...new Set(c.map((x) => x.expiration_date))].sort();
          setExpDates(dates);
          if (dates.length > 0 && !dates.includes(selectedExp)) {
            setSelectedExp(dates[0]);
          }
        }
      } catch (e) { console.error('options:', e); }
    })();
    return () => { cancelled = true; };
  }, [symbol, optType, tab]);

  useEffect(() => {
    if (!selectedExp || allContracts.length === 0) return;
    const filtered = allContracts
      .filter((c) => c.expiration_date === selectedExp)
      .sort((a, b) => parseFloat(a.strike_price) - parseFloat(b.strike_price));
    setContracts(filtered);
  }, [selectedExp, allContracts]);

  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => { if (!snap) setLoadError(true); }, 8000);
    return () => clearTimeout(timer);
  }, [snap]);

  if (!snap && loadError) return (
    <div className="p-4 sm:p-8 text-center">
      <div className="text-lg font-bold text-[#181D27] mb-2">No se encontraron datos para {symbol}</div>
      <div className="text-sm text-[#9CA3AF] mb-4">Es posible que el símbolo no esté disponible en el mercado o que el mercado esté cerrado.</div>
      <button onClick={onBack} className="px-6 py-2.5 bg-[#2A59C6] text-white rounded-xl text-sm font-semibold">← Volver</button>
    </div>
  );

  if (!snap) return <div className="p-4 sm:p-8 text-center text-[#9CA3AF]">Cargando {symbol}...</div>;

  const price = snap.latestTrade?.p || 0;
  const prevClose = snap.prevDailyBar?.c || 0;
  const change = price - prevClose;
  const pct = prevClose ? (change / prevClose) * 100 : 0;
  const isPos = pct >= 0;
  const dayBar = snap.dailyBar || {};
  const quote = snap.latestQuote || {};

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-4 sm:py-6 pb-20 sm:pb-6">
      <button onClick={onBack} className="flex items-center gap-2 text-sm font-semibold text-[#2A59C6] hover:underline mb-4 sm:mb-5">
        ← Volver a Inversiones
      </button>

      <div className="flex items-center gap-3 mb-3 sm:mb-4">
        <div className="w-11 h-11 sm:w-14 sm:h-14 rounded-xl bg-[#F5F5F5] flex items-center justify-center text-base sm:text-lg font-bold text-[#2A59C6]">
          {symbol.slice(0, 2)}
        </div>
        <div>
          <div className="text-xl sm:text-2xl font-extrabold">{symbol}</div>
          <div className="text-xs sm:text-sm text-[#6B7280]">{STOCK_NAMES[symbol] || ''}</div>
        </div>
      </div>

      <div className="mb-4 sm:mb-6">
        <div className="text-3xl sm:text-4xl font-extrabold">{formatPrice(price)}</div>
        <div className={`text-sm sm:text-base font-semibold ${isPos ? 'text-green-600' : 'text-red-600'}`}>
          {isPos ? '↑' : '↓'} {change.toFixed(2)} ({formatPct(pct)}) Hoy
        </div>
      </div>

      <div className="flex gap-0 border-b-2 border-[#E5E7EB] mb-5">
        <button onClick={() => setTab('accion')} className={`px-5 py-3 text-sm font-semibold border-b-2 -mb-0.5 ${tab === 'accion' ? 'text-[#2A59C6] border-[#2A59C6]' : 'text-[#6B7280] border-transparent'}`}>
          Acción
        </button>
        <button onClick={() => setTab('opciones')} className={`px-5 py-3 text-sm font-semibold border-b-2 -mb-0.5 ${tab === 'opciones' ? 'text-[#2A59C6] border-[#2A59C6]' : 'text-[#6B7280] border-transparent'}`}>
          Opciones
        </button>
        <button onClick={() => setTab('news')} className={`px-5 py-3 text-sm font-semibold border-b-2 -mb-0.5 ${tab === 'news' ? 'text-[#2A59C6] border-[#2A59C6]' : 'text-[#6B7280] border-transparent'}`}>
          Noticias
        </button>
      </div>

      {tab === 'accion' && (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6">
          <div>
            <div className="flex gap-1 mb-3">
              {['1D', '1S', '1M', '3M', '1A', 'Max'].map((p) => (
                <button key={p} onClick={() => setPeriod(p)} className={`px-3.5 py-1.5 rounded-full text-xs font-semibold ${period === p ? 'bg-[#2A59C6] text-white' : 'text-[#6B7280] hover:bg-[#F5F5F5]'}`}>
                  {p}
                </button>
              ))}
            </div>
            <div className="bg-white border border-[#E5E7EB] rounded-xl p-5 mb-5">
              <MiniChart bars={bars} isPos={isPos} />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3">
              <div className="bg-white border border-[#E5E7EB] rounded-lg p-3.5">
                <div className="text-[11px] text-[#6B7280] uppercase font-semibold tracking-wider">Cierre anterior</div>
                <div className="text-base font-bold">{formatPrice(prevClose)}</div>
              </div>
              <div className="bg-white border border-[#E5E7EB] rounded-lg p-3.5">
                <div className="text-[11px] text-[#6B7280] uppercase font-semibold tracking-wider">Apertura</div>
                <div className="text-base font-bold">{formatPrice(dayBar.o || 0)}</div>
              </div>
              <div className="bg-white border border-[#E5E7EB] rounded-lg p-3.5">
                <div className="text-[11px] text-[#6B7280] uppercase font-semibold tracking-wider">Volumen</div>
                <div className="text-base font-bold">{(dayBar.v || 0).toLocaleString()}</div>
              </div>
              <div className="bg-white border border-[#E5E7EB] rounded-lg p-3.5">
                <div className="text-[11px] text-[#6B7280] uppercase font-semibold tracking-wider">Máximo hoy</div>
                <div className="text-base font-bold text-green-600">{formatPrice(dayBar.h || 0)}</div>
              </div>
              <div className="bg-white border border-[#E5E7EB] rounded-lg p-3.5">
                <div className="text-[11px] text-[#6B7280] uppercase font-semibold tracking-wider">Mínimo hoy</div>
                <div className="text-base font-bold text-red-600">{formatPrice(dayBar.l || 0)}</div>
              </div>
              <div className="bg-white border border-[#E5E7EB] rounded-lg p-3.5">
                <div className="text-[11px] text-[#6B7280] uppercase font-semibold tracking-wider">VWAP</div>
                <div className="text-base font-bold">{dayBar.vw ? formatPrice(dayBar.vw) : '-'}</div>
              </div>
            </div>
            {/* Cerebro Analysis */}
            <StockAnalysis symbol={symbol} />
          </div>

          <div>
            <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 sticky top-5">
              <div className="flex border border-[#E5E7EB] rounded-lg overflow-hidden mb-4">
                <button onClick={() => setBuyMode('buy')} className={`flex-1 py-2.5 text-sm font-semibold ${buyMode === 'buy' ? 'bg-[#2A59C6] text-white' : 'text-[#6B7280]'}`}>Comprar</button>
                <button onClick={() => setBuyMode('sell')} className={`flex-1 py-2.5 text-sm font-semibold ${buyMode === 'sell' ? 'bg-[#2A59C6] text-white' : 'text-[#6B7280]'}`}>Vender</button>
              </div>
              <label className="block text-xs font-semibold text-[#6B7280] mb-1.5">Monto en USD</label>
              <div className="flex items-center border border-[#E5E7EB] rounded-lg px-3.5 py-2.5 mb-4">
                <span className="text-lg font-bold text-[#9CA3AF] mr-1">$</span>
                <input type="number" value={buyAmt} onChange={(e) => setBuyAmt(e.target.value)} placeholder="0.00" className="flex-1 outline-none text-xl font-bold" />
              </div>
              <div className="flex gap-1.5 mb-4">
                {[12, 25, 50, 100, 500].map((a) => (
                  <button key={a} onClick={() => setBuyAmt(String(a))} className="flex-1 py-2 border border-[#E5E7EB] rounded-md text-xs font-semibold text-[#2A59C6] hover:bg-[#E6EBF7]">${a}</button>
                ))}
              </div>
              {buyAmt && parseFloat(buyAmt) > 0 && parseFloat(buyAmt) < ALPACA_MIN_TRADE_USD && (
                <div className="bg-[#FEF2F2] border border-[#FCA5A5] rounded-lg p-3 mb-3 text-xs text-[#991B1B]">
                  Inversión mínima: <strong>${ALPACA_MIN_TRADE_USD} USD</strong>. Aumenta el monto para continuar.
                </div>
              )}
              {buyAmt && parseFloat(buyAmt) >= ALPACA_MIN_TRADE_USD && (() => {
                const amt = parseFloat(buyAmt);
                const c = calculateAlpacaCommission(amt);
                const total = amt + c.feeTotal;
                const tierLabel = c.tier === 'tier1' ? 'Plana $12–$50' : c.tier === 'tier2' ? 'Plana $50–$500' : c.tier === 'cap' ? 'Tope máximo' : '0,3% sobre el monto';
                return (
                  <div className="bg-[#FAFAFA] rounded-lg p-3.5 mb-3 text-xs">
                    <div className="flex justify-between py-0.5 text-[#6B7280]"><span>Monto invertido</span><span>{formatPrice(amt)}</span></div>
                    <div className="flex justify-between py-0.5 text-[#6B7280]"><span>Acciones estimadas</span><span>{(amt / price).toFixed(6)}</span></div>
                    <div className="flex justify-between py-0.5 text-[#6B7280]"><span>Comisión base <span className="text-[10px] text-[#9CA3AF]">({tierLabel})</span></span><span>${c.feeBase.toFixed(2)}</span></div>
                    <div className="flex justify-between py-0.5 text-[#6B7280]"><span>IVA 19%</span><span>${c.iva.toFixed(2)}</span></div>
                    <div className="flex justify-between pt-2.5 mt-1.5 border-t border-[#E5E7EB] font-bold"><span>Total a debitar</span><span>{formatPrice(total)}</span></div>
                    <div className="mt-2 text-[10px] text-[#9CA3AF] leading-snug">Recibirás factura electrónica al correo. Comisión efectiva: {c.effectivePct.toFixed(2)}%.</div>
                  </div>
                );
              })()}
              <div className="bg-[#FEF3C7] rounded-lg px-3.5 py-2.5 text-xs text-center mb-3 text-[#92400E]">
                ✨ Invierte desde $12 USD — fraccionadas · comisión desde $0,50
              </div>
              <button
                disabled={!buyAmt || parseFloat(buyAmt) < ALPACA_MIN_TRADE_USD || orderLoading}
                onClick={async () => {
                  setOrderLoading(true);
                  setOrderResult(null);
                  setOrderError(null);
                  try {
                    const result = await placeStockOrder({
                      symbol,
                      notional: parseFloat(buyAmt),
                      side: buyMode,
                      accountId,
                    });
                    setOrderResult(result);
                  } catch (e) {
                    setOrderError(e.response?.data?.message || e.message || 'Error al procesar la orden');
                  }
                  setOrderLoading(false);
                }}
                className={`w-full py-3.5 rounded-xl text-sm font-bold text-white ${buyMode === 'buy' ? 'bg-[#2A59C6]' : 'bg-red-500'} disabled:opacity-35 disabled:cursor-not-allowed`}
              >
                {orderLoading ? 'Procesando...' : `${buyMode === 'buy' ? 'Comprar' : 'Vender'} ${symbol}`}
              </button>
              {orderResult && (
                <div className="mt-3 bg-green-50 border border-green-200 rounded-lg p-3 text-xs">
                  <div className="font-bold text-green-700 mb-1">Orden enviada</div>
                  <div className="text-green-600">ID: {orderResult.id?.slice(0, 8)}...</div>
                  <div className="text-green-600">Estado: {orderResult.status}</div>
                  <div className="text-green-600">{orderResult.side?.toUpperCase()} {orderResult.symbol} · ${orderResult.notional || orderResult.qty}</div>
                </div>
              )}
              {orderError && (
                <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3 text-xs">
                  <div className="font-bold text-red-700 mb-1">Error</div>
                  <div className="text-red-600">{orderError}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {tab === 'opciones' && (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6">
          <div>
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <div className="flex gap-2">
                <button onClick={() => { setOptType('call'); setSelectedOption(null); }} className={`px-5 py-2 rounded-full text-xs font-semibold border ${optType === 'call' ? 'bg-green-600 text-white border-green-600' : 'border-[#E5E7EB] text-[#6B7280]'}`}>Calls</button>
                <button onClick={() => { setOptType('put'); setSelectedOption(null); }} className={`px-5 py-2 rounded-full text-xs font-semibold border ${optType === 'put' ? 'bg-red-600 text-white border-red-600' : 'border-[#E5E7EB] text-[#6B7280]'}`}>Puts</button>
              </div>
              {expDates.length > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-[#6B7280]">Vencimiento:</span>
                  <select
                    value={selectedExp}
                    onChange={(e) => { setSelectedExp(e.target.value); setSelectedOption(null); }}
                    className="border border-[#E5E7EB] rounded-lg px-3 py-1.5 text-sm font-semibold text-[#2A59C6] outline-none focus:border-[#2A59C6] cursor-pointer"
                  >
                    {expDates.map((d) => (
                      <option key={d} value={d}>
                        {new Date(d + 'T12:00:00').toLocaleDateString('es', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </option>
                    ))}
                  </select>
                  <span className="text-xs text-[#9CA3AF]">({expDates.length} fechas)</span>
                </div>
              )}
            </div>

            <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden max-h-[500px] overflow-y-auto">
              <div className="grid grid-cols-[1fr_1.2fr_1fr_0.8fr] px-4 py-2.5 bg-[#FAFAFA] text-[11px] font-bold text-[#6B7280] uppercase tracking-wider sticky top-0 z-10">
                <span>Strike Price</span><span>Prima (Último)</span><span>Open Interest</span><span>Tipo</span>
              </div>
              {contracts.length === 0 ? (
                <div className="p-6 text-center text-sm text-[#9CA3AF]">Cargando contratos...</div>
              ) : (
                contracts.map((c) => {
                  const strikeNum = parseFloat(c.strike_price);
                  const isITM = optType === 'call' ? strikeNum < price : strikeNum > price;
                  const isSelected = selectedOption?.symbol === c.symbol;
                  return (
                    <div
                      key={c.symbol}
                      onClick={() => { setSelectedOption(c); setOptQty('1'); }}
                      className={`grid grid-cols-[1fr_1.2fr_1fr_0.8fr] px-4 py-3 border-t border-[#F5F5F5] text-sm cursor-pointer transition ${isSelected ? 'bg-[#E6EBF7] border-l-4 border-l-[#2A59C6]' : isITM ? 'bg-green-50 hover:bg-green-100' : 'hover:bg-[#FAFAFA]'}`}
                    >
                      <div>
                        <span className="font-bold text-[#2A59C6]">${c.strike_price}</span>
                        {isITM && <span className="ml-2 text-[10px] font-bold text-green-600 bg-green-100 px-1.5 py-0.5 rounded">ITM</span>}
                      </div>
                      <div>
                        <span className="font-semibold">{c.close_price ? `$${Number(c.close_price).toFixed(2)}` : '-'}</span>
                        {c.close_price && <span className="text-xs text-[#9CA3AF] ml-1">x100 = ${(Number(c.close_price) * 100).toFixed(0)}</span>}
                      </div>
                      <span className="text-[#6B7280]">{c.open_interest || '-'}</span>
                      <span className={`text-xs font-semibold ${optType === 'call' ? 'text-green-600' : 'text-red-600'}`}>{optType === 'call' ? 'Call' : 'Put'}</span>
                    </div>
                  );
                })
              )}
            </div>
            <div className="mt-3 text-xs text-[#9CA3AF]">
              ITM = In The Money (precio actual: {formatPrice(price)}) · Selecciona un contrato para operar
            </div>
          </div>

          {/* Options order panel */}
          <div>
            <div className="bg-white border border-[#E5E7EB] rounded-2xl p-6 sticky top-5">
              {!selectedOption ? (
                <div className="text-center py-8">
                  <div className="text-4xl mb-3">👆</div>
                  <div className="text-sm font-semibold text-[#6B7280]">Selecciona un contrato</div>
                  <div className="text-xs text-[#9CA3AF] mt-1">Haz click en una opción para operar</div>
                </div>
              ) : (
                <>
                  <div className="flex border border-[#E5E7EB] rounded-lg overflow-hidden mb-4">
                    <button onClick={() => setOptMode('buy')} className={`flex-1 py-2.5 text-sm font-semibold ${optMode === 'buy' ? 'bg-[#2A59C6] text-white' : 'text-[#6B7280]'}`}>Comprar</button>
                    <button onClick={() => setOptMode('sell')} className={`flex-1 py-2.5 text-sm font-semibold ${optMode === 'sell' ? 'bg-orange-500 text-white' : 'text-[#6B7280]'}`}>Emitir</button>
                  </div>

                  <div className="bg-[#FAFAFA] rounded-lg p-3.5 mb-4 space-y-2">
                    <div className="flex justify-between text-xs">
                      <span className="text-[#6B7280]">Contrato</span>
                      <span className="font-bold text-[#2A59C6]">{symbol} {optType === 'call' ? 'Call' : 'Put'} ${selectedOption.strike_price}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-[#6B7280]">Vencimiento</span>
                      <span className="font-semibold">{new Date(selectedOption.expiration_date + 'T12:00:00').toLocaleDateString('es', { day: '2-digit', month: 'long', year: 'numeric' })}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-[#6B7280]">Prima por acción</span>
                      <span className="font-semibold">{selectedOption.close_price ? `$${Number(selectedOption.close_price).toFixed(2)}` : 'N/A'}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-[#6B7280]">Multiplicador</span>
                      <span className="font-semibold">x100 acciones</span>
                    </div>
                  </div>

                  <label className="block text-xs font-semibold text-[#6B7280] mb-1.5">Contratos</label>
                  <div className="flex items-center border border-[#E5E7EB] rounded-lg px-3.5 py-2.5 mb-3">
                    <button onClick={() => setOptQty(String(Math.max(1, parseInt(optQty || '1') - 1)))} className="w-8 h-8 rounded-lg bg-[#F5F5F5] flex items-center justify-center text-lg font-bold text-[#6B7280] hover:bg-[#E5E7EB]">-</button>
                    <input type="number" value={optQty} onChange={(e) => setOptQty(e.target.value)} min="1" className="flex-1 outline-none text-xl font-bold text-center" />
                    <button onClick={() => setOptQty(String(parseInt(optQty || '1') + 1))} className="w-8 h-8 rounded-lg bg-[#F5F5F5] flex items-center justify-center text-lg font-bold text-[#6B7280] hover:bg-[#E5E7EB]">+</button>
                  </div>

                  {selectedOption.close_price && parseInt(optQty) > 0 && (
                    <div className="bg-[#FAFAFA] rounded-lg p-3.5 mb-3 text-xs space-y-1">
                      <div className="flex justify-between text-[#6B7280]">
                        <span>Prima por contrato</span>
                        <span>${(Number(selectedOption.close_price) * 100).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-[#6B7280]">
                        <span>Contratos</span>
                        <span>x{parseInt(optQty) || 1}</span>
                      </div>
                      <div className="flex justify-between text-[#6B7280]">
                        <span>Comisión</span>
                        <span className="text-green-600 font-bold">$0 GRATIS</span>
                      </div>
                      <div className="flex justify-between pt-2 mt-1 border-t border-[#E5E7EB] font-bold text-sm">
                        <span>{optMode === 'buy' ? 'Costo total' : 'Prima recibida'}</span>
                        <span>{formatPrice(Number(selectedOption.close_price) * 100 * (parseInt(optQty) || 1))}</span>
                      </div>
                    </div>
                  )}

                  {optMode === 'buy' ? (
                    <div className="bg-[#EFF6FF] rounded-lg px-3.5 py-2.5 text-xs text-center mb-3 text-[#1E40AF]">
                      Derecho a {optType === 'call' ? 'comprar' : 'vender'} {(parseInt(optQty) || 1) * 100} acciones de {symbol} a ${selectedOption.strike_price}
                    </div>
                  ) : (
                    <div className="bg-[#FFF7ED] rounded-lg px-3.5 py-2.5 text-xs text-center mb-3 text-[#9A3412]">
                      Obligación de {optType === 'call' ? 'vender' : 'comprar'} {(parseInt(optQty) || 1) * 100} acciones de {symbol} a ${selectedOption.strike_price}
                    </div>
                  )}

                  <button
                    disabled={optOrderLoading}
                    onClick={async () => {
                      setOptOrderLoading(true);
                      setOptOrderResult(null);
                      setOptOrderError(null);
                      try {
                        const result = await placeOptionOrder({
                          symbol: selectedOption.symbol,
                          qty: parseInt(optQty) || 1,
                          side: optMode === 'buy' ? 'buy' : 'sell',
                          accountId,
                        });
                        setOptOrderResult(result);
                      } catch (e) {
                        const code = e.response?.data?.code;
                        const msg = e.response?.data?.message || e.message || 'Error al procesar la orden';
                        setOptOrderError(code === 40310000 ? 'Opciones no habilitadas en sandbox. Contactar soporte Alpaca.' : msg);
                      }
                      setOptOrderLoading(false);
                    }}
                    className={`w-full py-3.5 rounded-xl text-sm font-bold text-white ${optMode === 'buy' ? 'bg-[#2A59C6]' : 'bg-orange-500'} disabled:opacity-35`}
                  >
                    {optOrderLoading ? 'Procesando...' : optMode === 'buy' ? `Comprar ${parseInt(optQty) || 1} contrato${(parseInt(optQty) || 1) > 1 ? 's' : ''}` : `Emitir ${parseInt(optQty) || 1} contrato${(parseInt(optQty) || 1) > 1 ? 's' : ''}`}
                  </button>
                  {optOrderResult && (
                    <div className="mt-3 bg-green-50 border border-green-200 rounded-lg p-3 text-xs">
                      <div className="font-bold text-green-700 mb-1">Orden enviada</div>
                      <div className="text-green-600">ID: {optOrderResult.id?.slice(0, 8)}...</div>
                      <div className="text-green-600">Estado: {optOrderResult.status}</div>
                    </div>
                  )}
                  {optOrderError && (
                    <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3 text-xs">
                      <div className="font-bold text-red-700 mb-1">Error</div>
                      <div className="text-red-600">{optOrderError}</div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {tab === 'news' && (
        <div className="space-y-3">
          {news.length === 0 && !newsError && <div className="text-sm text-[#9CA3AF]">Cargando noticias...</div>}
          {news.length === 0 && newsError && (
            <div className="bg-white border border-[#E5E7EB] rounded-xl p-8 text-center">
              <div className="text-lg font-bold text-[#181D27] mb-2">Noticias no disponibles</div>
              <div className="text-sm text-[#6B7280] mb-4">El plan actual de datos no incluye noticias del mercado.</div>
              <a href={`https://finance.yahoo.com/quote/${symbol}/news/`} target="_blank" rel="noopener noreferrer" className="inline-block px-5 py-2.5 bg-[#2A59C6] text-white rounded-xl text-sm font-semibold hover:bg-[#1E4BB8] transition">
                Ver noticias en Yahoo Finance
              </a>
            </div>
          )}
          {news.map((n) => (
            <a key={n.id} href={n.url} target="_blank" rel="noopener noreferrer" className="block bg-white border border-[#E5E7EB] rounded-xl p-5 hover:border-[#2A59C6] transition">
              <div className="flex items-start gap-4">
                {n.images?.[0]?.url && (
                  <img src={n.images[0].url} alt="" className="w-20 h-20 rounded-lg object-cover flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-[#6B7280] mb-1">{n.source} · {new Date(n.created_at).toLocaleDateString('es')}</div>
                  <div className="text-sm font-semibold text-[#181D27] mb-1.5">{n.headline}</div>
                  <div className="text-xs text-[#6B7280] line-clamp-2">{n.summary}</div>
                </div>
              </div>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

function Portfolio({ onSelectStock, accountId }) {
  const [account, setAccount] = useState(null);
  const [positions, setPositions] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loadingPortfolio, setLoadingPortfolio] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [acc, pos, ord] = await Promise.all([
          getAccount(accountId),
          getPositions(accountId),
          getOrders('all', 20, accountId),
        ]);
        if (!cancelled) {
          setAccount(acc);
          setPositions(pos);
          setOrders(ord);
        }
      } catch (e) {
        console.error('portfolio:', e);
      } finally {
        if (!cancelled) setLoadingPortfolio(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loadingPortfolio) return <div className="text-center py-10 text-[#9CA3AF]">Cargando portafolio...</div>;

  const equity = parseFloat(account?.equity || 0);
  const cash = parseFloat(account?.cash || 0);
  const buyingPower = parseFloat(account?.buying_power || 0);
  const portfolioValue = parseFloat(account?.portfolio_value || 0);
  const totalPL = positions.reduce((sum, p) => sum + parseFloat(p.unrealized_pl || 0), 0);
  const totalPLPct = equity > 0 ? (totalPL / (equity - totalPL)) * 100 : 0;

  return (
    <div>
      {/* Account summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-4 sm:p-5">
          <div className="text-[10px] sm:text-[11px] text-[#6B7280] uppercase font-semibold tracking-wider mb-1">Valor del portafolio</div>
          <div className="text-lg sm:text-2xl font-extrabold text-[#181D27]">{formatPrice(portfolioValue)}</div>
        </div>
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-4 sm:p-5">
          <div className="text-[10px] sm:text-[11px] text-[#6B7280] uppercase font-semibold tracking-wider mb-1">Efectivo</div>
          <div className="text-lg sm:text-2xl font-extrabold text-[#181D27]">{formatPrice(cash)}</div>
        </div>
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-4 sm:p-5">
          <div className="text-[10px] sm:text-[11px] text-[#6B7280] uppercase font-semibold tracking-wider mb-1">Poder de compra</div>
          <div className="text-lg sm:text-2xl font-extrabold text-[#2A59C6]">{formatPrice(buyingPower)}</div>
        </div>
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-4 sm:p-5">
          <div className="text-[10px] sm:text-[11px] text-[#6B7280] uppercase font-semibold tracking-wider mb-1">Ganancia / Pérdida</div>
          <div className={`text-lg sm:text-2xl font-extrabold ${totalPL >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {totalPL >= 0 ? '+' : ''}{formatPrice(totalPL)}
          </div>
          <div className={`text-xs font-semibold ${totalPL >= 0 ? 'text-green-600' : 'text-red-600'}`}>{formatPct(totalPLPct)}</div>
        </div>
      </div>

      {/* Positions */}
      <h2 className="text-lg font-bold mb-4">Mis Posiciones</h2>
      {positions.length === 0 ? (
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-8 text-center mb-8">
          <div className="text-sm text-[#9CA3AF]">No tienes posiciones abiertas. Compra una acción para empezar.</div>
        </div>
      ) : (
        <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden mb-8 overflow-x-auto">
          <div className="min-w-[700px]">
          <div className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr_1fr] px-5 py-3 bg-[#FAFAFA] text-[11px] font-bold text-[#6B7280] uppercase tracking-wider">
            <span>Activo</span><span className="text-right">Cantidad</span><span className="text-right">Precio actual</span><span className="text-right">Costo promedio</span><span className="text-right">Valor de mercado</span><span className="text-right">Ganancia</span>
          </div>
          {positions.map((p) => {
            const pl = parseFloat(p.unrealized_pl || 0);
            const plPct = parseFloat(p.unrealized_plpc || 0) * 100;
            const qty = parseFloat(p.qty || p.qty_available || 0);
            const currentPrice = parseFloat(p.current_price || 0);
            const avgEntry = parseFloat(p.avg_entry_price || 0);
            const marketValue = parseFloat(p.market_value || 0);
            return (
              <div
                key={p.asset_id}
                onClick={() => onSelectStock(p.symbol)}
                className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr_1fr] px-5 py-4 border-t border-[#F5F5F5] cursor-pointer hover:bg-[#FAFAFA] transition items-center"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-[#F5F5F5] flex items-center justify-center text-xs font-bold text-[#2A59C6]">{p.symbol.slice(0, 2)}</div>
                  <div>
                    <div className="text-sm font-bold text-[#2A59C6]">{p.symbol}</div>
                    <div className="text-xs text-[#6B7280] truncate">{STOCK_NAMES[p.symbol] || ''}</div>
                  </div>
                </div>
                <div className="text-right text-sm font-semibold">{qty < 1 ? qty.toFixed(6) : qty.toFixed(2)}</div>
                <div className="text-right text-sm font-semibold">{formatPrice(currentPrice)}</div>
                <div className="text-right text-sm text-[#6B7280]">{formatPrice(avgEntry)}</div>
                <div className="text-right text-sm font-semibold">{formatPrice(marketValue)}</div>
                <div className="text-right">
                  <div className={`text-sm font-bold ${pl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {pl >= 0 ? '+' : ''}{formatPrice(pl)}
                  </div>
                  <div className={`text-xs font-semibold ${pl >= 0 ? 'text-green-600' : 'text-red-600'}`}>{formatPct(plPct)}</div>
                </div>
              </div>
            );
          })}
          </div>
        </div>
      )}

      {/* Recent orders */}
      <h2 className="text-lg font-bold mb-4">Órdenes Recientes</h2>
      {orders.length === 0 ? (
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-8 text-center">
          <div className="text-sm text-[#9CA3AF]">No hay órdenes recientes.</div>
        </div>
      ) : (
        <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden overflow-x-auto">
          <div className="min-w-[650px]">
          <div className="grid grid-cols-[1.5fr_0.8fr_1fr_1fr_1fr_1fr] px-5 py-3 bg-[#FAFAFA] text-[11px] font-bold text-[#6B7280] uppercase tracking-wider">
            <span>Activo</span><span>Tipo</span><span className="text-right">Cantidad</span><span className="text-right">Precio</span><span className="text-right">Total</span><span className="text-right">Estado</span>
          </div>
          {orders.slice(0, 15).map((o) => {
            const filledQty = parseFloat(o.filled_qty || 0);
            const filledAvg = parseFloat(o.filled_avg_price || 0);
            const notional = o.notional ? parseFloat(o.notional) : filledQty * filledAvg;
            const statusMap = {
              new: 'Nuevo', accepted: 'Aceptada', filled: 'Ejecutada',
              partially_filled: 'Parcial', canceled: 'Cancelada',
              expired: 'Expirada', rejected: 'Rechazada', pending_new: 'Pendiente',
            };
            const statusColor = {
              filled: 'text-green-600 bg-green-50', accepted: 'text-blue-600 bg-blue-50',
              new: 'text-blue-600 bg-blue-50', canceled: 'text-[#9CA3AF] bg-[#F5F5F5]',
              rejected: 'text-red-600 bg-red-50', expired: 'text-[#9CA3AF] bg-[#F5F5F5]',
              partially_filled: 'text-orange-600 bg-orange-50', pending_new: 'text-blue-600 bg-blue-50',
            };
            return (
              <div key={o.id} className="grid grid-cols-[1.5fr_0.8fr_1fr_1fr_1fr_1fr] px-5 py-3.5 border-t border-[#F5F5F5] items-center text-sm">
                <div>
                  <span className="font-bold text-[#2A59C6]">{o.symbol}</span>
                  <span className="text-xs text-[#9CA3AF] ml-2">{new Date(o.created_at).toLocaleDateString('es', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                </div>
                <div>
                  <span className={`text-xs font-bold px-2 py-1 rounded ${o.side === 'buy' ? 'text-green-700 bg-green-50' : 'text-red-700 bg-red-50'}`}>
                    {o.side === 'buy' ? 'Compra' : 'Venta'}
                  </span>
                </div>
                <div className="text-right font-semibold">
                  {filledQty > 0 ? (filledQty < 1 ? filledQty.toFixed(6) : filledQty.toFixed(2)) : (o.notional ? `$${parseFloat(o.notional).toFixed(2)}` : o.qty)}
                </div>
                <div className="text-right text-[#6B7280]">{filledAvg > 0 ? formatPrice(filledAvg) : '-'}</div>
                <div className="text-right font-semibold">{notional > 0 ? formatPrice(notional) : '-'}</div>
                <div className="text-right">
                  <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${statusColor[o.status] || 'text-[#6B7280] bg-[#F5F5F5]'}`}>
                    {statusMap[o.status] || o.status}
                  </span>
                </div>
              </div>
            );
          })}
          </div>
        </div>
      )}
    </div>
  );
}

const LEVELS = [
  { name: 'Novato', minXP: 0 },
  { name: 'Inversionista', minXP: 3 },
  { name: 'Trader', minXP: 6 },
  { name: 'Experto', minXP: 9 },
];

function AchievementMini() {
  const streak = (() => { try { return JSON.parse(localStorage.getItem('fp_invest_streak') || '{}').weeks || 0; } catch (e) { return 0; } })();
  const hasProfile = !!localStorage.getItem('fp_investor_profile');
  const roboUsed = !!localStorage.getItem('fp_robo_used');
  const viewedStocks = getViewedCount();
  const referrals = parseInt(localStorage.getItem('fp_referrals') || '0');

  // Count unlocked (simplified — no API calls, just local data)
  let count = 0;
  if (hasProfile) count++;
  if (roboUsed) count++;
  if (viewedStocks >= 10) count++;
  if (referrals >= 1) count++;
  if (referrals >= 5) count++;

  const level = LEVELS.slice().reverse().find(l => count >= l.minXP) || LEVELS[0];
  const icon = level.name === 'Novato' ? '🌱' : level.name === 'Inversionista' ? '📈' : level.name === 'Trader' ? '⚡' : '👑';

  if (count === 0 && streak === 0) return null;

  return (
    <div className="mb-3 flex items-center gap-3 text-xs text-[#6B7280]">
      <span className="flex items-center gap-1 bg-[#F0F4FF] text-[#2A59C6] px-2.5 py-1 rounded-full font-semibold">
        {icon} {level.name}
      </span>
      {streak > 0 && (
        <span className="flex items-center gap-1 bg-[#FFF7ED] text-[#EA580C] px-2.5 py-1 rounded-full font-semibold">
          🔥 {streak} {streak === 1 ? 'semana' : 'semanas'}
        </span>
      )}
      <span>{count} logros</span>
    </div>
  );
}


function getMarketStatus() {
  const now = new Date();
  const tzName = now.toLocaleString('en-US', { timeZone: 'America/New_York', timeZoneName: 'short' });
  const dst = tzName.includes('EDT');
  const bvcOpen = dst ? '8:30 AM COT' : '9:30 AM COT';
  const bvcClose = dst ? '3:00 PM COT' : '4:00 PM COT';
  const ny = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
  const day = ny.getDay();
  const tm = ny.getHours() * 60 + ny.getMinutes();
  if (day === 0 || day === 6) return { open: false, bvc: `Cerrado · reabre lunes ${bvcOpen}`, nyse: 'Cerrado · reabre lunes 9:30 AM ET' };
  if (tm < 570) return { open: false, bvc: `Cerrado · abre hoy ${bvcOpen}`, nyse: 'Cerrado · abre hoy 9:30 AM ET' };
  if (tm >= 960) return { open: false, bvc: `Cerrado · reabre mañana ${bvcOpen}`, nyse: 'Cerrado · reabre mañana 9:30 AM ET' };
  return { open: true, bvc: `Abierto · cierra ${bvcClose}`, nyse: 'Abierto · cierra 4:00 PM ET' };
}

function NyseStatusBanner() {
  const mkt = getMarketStatus();
  return (
    <div className={`rounded-xl p-3 mb-4 flex items-center gap-2 text-xs font-semibold ${mkt.open ? 'bg-[#ECFDF5] border border-[#10B981] text-[#065F46]' : 'bg-[#FEF2F2] border border-[#FCA5A5] text-[#991B1B]'}`}>
      <span className={`w-2.5 h-2.5 rounded-full ${mkt.open ? 'bg-[#10B981]' : 'bg-[#DC2626]'}`}></span>
      NYSE · {mkt.nyse}
    </div>
  );
}

function ColombiaMarketPlaceholder() {
  const [colCat, setColCat] = useState('populares');
  const [modal, setModal] = useState(null);
  const populares = [
    { ticker: 'ECOPETROL', name: 'Ecopetrol S.A.', price: 2400, change: 0.52 },
    { ticker: 'BCOLOMBIA', name: 'Bancolombia', price: 35200, change: 1.18 },
    { ticker: 'ISA', name: 'Interconexión Eléctrica', price: 19850, change: -0.34 },
    { ticker: 'GRUPOSURA', name: 'Grupo SURA', price: 28100, change: 0.87 },
    { ticker: 'NUTRESA', name: 'Grupo Nutresa', price: 78900, change: 2.41 },
    { ticker: 'CEMARGOS', name: 'Cementos Argos', price: 4180, change: -1.05 },
  ];
  const masActivas = [
    { ticker: 'NUTRESA', name: 'Grupo Nutresa', price: 78900, change: 2.41 },
    { ticker: 'BCOLOMBIA', name: 'Bancolombia', price: 35200, change: 1.18 },
    { ticker: 'CEMARGOS', name: 'Cementos Argos', price: 4180, change: -1.05 },
    { ticker: 'GRUPOSURA', name: 'Grupo SURA', price: 28100, change: 0.87 },
    { ticker: 'ECOPETROL', name: 'Ecopetrol S.A.', price: 2400, change: 0.52 },
    { ticker: 'ISA', name: 'Interconexión Eléctrica', price: 19850, change: -0.34 },
  ];
  const fondos = [
    { ticker: 'RENTAR-II', name: 'Fondo Rentar II', tipo: 'Renta Fija', rendimiento: '8.5% E.A.', min: '$50.000 COP' },
    { ticker: 'ACCIONES-CO', name: 'Acciones Colombia', tipo: 'Renta Variable', rendimiento: '12.3% últ. 12m', min: '$100.000 COP' },
    { ticker: 'MIXTO-MOD', name: 'Fondo Mixto Moderado', tipo: 'Mixto', rendimiento: '9.8% E.A.', min: '$50.000 COP' },
    { ticker: 'LIQUIDEZ', name: 'Fondo Liquidez', tipo: 'Mercado Monetario', rendimiento: '7.2% E.A.', min: '$10.000 COP' },
  ];
  const fmtCop = (n) => `$${n.toLocaleString('es-CO')}`;
  function getBvcStatus() {
    const mkt = getMarketStatus();
    return { open: mkt.open, msg: mkt.bvc };
  }
  function getFondosCutoff() {
    const colTime = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Bogota' }));
    const day = colTime.getDay();
    const tm = colTime.getHours() * 60 + colTime.getMinutes();
    if (day === 0 || day === 6) return 'Órdenes 24/7. Tu orden se ejecutará el lunes hábil con NAV del día.';
    if (tm < 720) return 'Órdenes 24/7. Antes de 12:00 PM COT se ejecutan hoy con NAV del día.';
    return 'Órdenes 24/7. Pasado el corte de 12:00 PM, tu orden se ejecutará mañana hábil.';
  }
  const mkt = getBvcStatus();
  const cutoffMsg = getFondosCutoff();
  const cats = [
    { id: 'populares', label: 'Populares' },
    { id: 'fondos', label: 'Fondos' },
    { id: 'movers', label: 'Más activas' },
  ];
  const onStock = (a) => {
    if (!mkt.open) {
      setModal({ title: 'Mercado cerrado', body: mkt.msg + '. Podrás comprar acciones colombianas cuando el mercado reabra.' });
      return;
    }
    setModal({ title: `Comprar ${a.ticker}`, body: `La compra de acciones colombianas estará disponible cuando se active el acuerdo con Progresión SCB. Precio actual: ${fmtCop(a.price)}.` });
  };
  const onFondo = (f) => {
    setModal({ title: `Invertir en ${f.name}`, body: `La inversión en ${f.name} (${f.tipo}) estará disponible cuando se active el acuerdo con Progresión SCB. Rendimiento histórico: ${f.rendimiento}. Inversión mínima: ${f.min}. ${cutoffMsg}` });
  };
  const renderStocks = (list) => (
    <div className="space-y-2">
      {list.map((a) => (
        <div key={a.ticker} onClick={() => onStock(a)} className="bg-white border border-[#E5E7EB] rounded-xl p-3 flex items-center justify-between cursor-pointer hover:border-[#2A59C6] transition">
          <div>
            <div className="text-sm font-bold text-[#0A2540]">{a.ticker}</div>
            <div className="text-xs text-[#6B7280]">{a.name}</div>
          </div>
          <div className="text-right">
            <div className="text-sm font-bold">{fmtCop(a.price)}</div>
            <div className={`text-xs font-semibold ${a.change >= 0 ? 'text-[#059669]' : 'text-[#DC2626]'}`}>
              {a.change >= 0 ? '+' : ''}{a.change.toFixed(2)}%
            </div>
          </div>
        </div>
      ))}
    </div>
  );
  return (
    <div className="space-y-4">
      <div className={`rounded-xl p-3 flex items-center gap-2 text-xs font-semibold ${mkt.open ? 'bg-[#ECFDF5] border border-[#10B981] text-[#065F46]' : 'bg-[#FEF2F2] border border-[#FCA5A5] text-[#991B1B]'}`}>
        <span className={`w-2.5 h-2.5 rounded-full ${mkt.open ? 'bg-[#10B981]' : 'bg-[#DC2626]'}`}></span>
        BVC · {mkt.msg}
      </div>
      <div className="bg-[#FFFBF0] border border-[#FCD34D] rounded-xl p-3 text-xs text-[#92400E]">
        Datos de demostración
      </div>
      <div className="flex gap-2 flex-wrap">
        {cats.map((c) => (
          <button key={c.id} onClick={() => setColCat(c.id)} className={`px-4.5 py-2 border rounded-full text-xs font-semibold ${colCat === c.id ? 'bg-[#2A59C6] text-white border-[#2A59C6]' : 'border-[#E5E7EB] text-[#6B7280] bg-white hover:border-[#2A59C6] hover:text-[#2A59C6]'} transition`}>
            {c.label}
          </button>
        ))}
      </div>
      {colCat === 'fondos' && (
        <div className="bg-[#EFF6FF] border border-[#BFDBFE] rounded-xl p-3 text-xs text-[#1E40AF]">
          ℹ️ {cutoffMsg}
        </div>
      )}
      {colCat === 'populares' && renderStocks(populares)}
      {colCat === 'movers' && renderStocks(masActivas)}
      {colCat === 'fondos' && (
        <div className="space-y-2">
          {fondos.map((f) => (
            <div key={f.ticker} onClick={() => onFondo(f)} className="bg-white border border-[#E5E7EB] rounded-xl p-3 flex items-center justify-between cursor-pointer hover:border-[#2A59C6] transition">
              <div className="flex-1">
                <div className="text-sm font-bold text-[#0A2540]">{f.name}</div>
                <div className="text-xs text-[#6B7280]">{f.tipo} · desde {f.min}</div>
              </div>
              <div className="text-right">
                <div className="text-sm font-bold text-[#059669]">{f.rendimiento}</div>
                <div className="text-xs text-[#9CA3AF]">rendimiento</div>
              </div>
            </div>
          ))}
        </div>
      )}
      {modal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setModal(null)}>
          <div className="bg-white rounded-2xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-[#0A2540] mb-2">{modal.title}</h3>
            <p className="text-sm text-[#6B7280] mb-4">{modal.body}</p>
            <button onClick={() => setModal(null)} className="w-full py-2.5 bg-[#2A59C6] text-white rounded-xl text-sm font-semibold hover:bg-[#1E4BB8] transition">
              Entendido
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Investments() {
  const snap = useSnapshot(state);
  const userEmail = snap.user?.email || '';
  const accountId = getAlpacaAccountId(userEmail);

  const [snapshots, setSnapshots] = useState({});
  const [movers, setMovers] = useState([]);
  const [analyzed, setAnalyzed] = useState([]); // símbolos con análisis del Cerebro (chip "Analizadas")
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');
  const [geoFilter, setGeoFilter] = useState('internacional');
  const [cat, setCat] = useState('popular');
  const [loading, setLoading] = useState(true);
  const [mainTab, setMainTab] = useState('mercado');
  // Ticker popover — null o symbol (string). Cuando hay symbol, renderiza el popup.
  const [tickerPopover, setTickerPopover] = useState(null);

  // Investor profile / onboarding
  const [investorProfile, setInvestorProfile] = useState(() => {
    try {
      const saved = localStorage.getItem('fp_investor_profile');
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });
  const [showOnboarding, setShowOnboarding] = useState(!investorProfile);

  const [showRoboAdvisor, setShowRoboAdvisor] = useState(false);

  const handleOnboardingComplete = (profile) => {
    if (profile) {
      setInvestorProfile(profile);
    }
    setShowOnboarding(false);
  };

  // Carga la lista de acciones analizadas (chip "Analizadas 🧠"). Se auto-actualiza:
  // cada acción que publiquemos en el banco del backend aparece sola, sin tocar el frontend.
  useEffect(() => {
    getAvailableSymbols()
      .then((syms) => { if (Array.isArray(syms)) setAnalyzed(syms); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetchPrices = async () => {
      try {
        const all = [...new Set([...POPULAR_SYMBOLS, ...ETF_SYMBOLS, ...analyzed])];
        const snaps = await getSnapshots(all);
        if (!cancelled) setSnapshots(snaps);
        const m = await getMostActives(8);
        if (!cancelled) setMovers(m.map((x) => x.symbol));
      } catch (e) {
        console.error('init:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchPrices();
    const interval = setInterval(fetchPrices, 30000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [analyzed]);

  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (!search || search.length < 1) { setSearchResults([]); return; }
    const lower = search.toLowerCase();

    // Search in ALL known stocks (ticker + company name)
    const allKnown = Object.entries(STOCK_NAMES);
    const matched = allKnown.filter(([ticker, name]) =>
      ticker.toLowerCase().includes(lower) || name.toLowerCase().includes(lower)
    ).map(([ticker]) => ticker);
    setSearchResults(matched.slice(0, 12));

    // Also try direct Alpaca lookup for unknown tickers
    if (search.length >= 2 && matched.length === 0) {
      const timer = setTimeout(async () => {
        setSearching(true);
        try {
          const remote = await searchAssets(search);
          const remoteSymbols = remote.map((r) => r.symbol);
          if (remoteSymbols.length > 0) {
            setSearchResults(remoteSymbols.slice(0, 12));
          }
        } catch { /* keep local results */ }
        setSearching(false);
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [search, movers]);

  if (showRoboAdvisor && investorProfile?.portfolio && PORTFOLIOS[investorProfile.portfolio]) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-4 sm:py-6 pb-20 sm:pb-6">
        <RoboAdvisor
          portfolio={PORTFOLIOS[investorProfile.portfolio]}
          onClose={() => setShowRoboAdvisor(false)}
        />
      </div>
    );
  }

  if (showOnboarding) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-4 sm:py-6 pb-20 sm:pb-6">
        <InvestorOnboarding onComplete={handleOnboardingComplete} />
      </div>
    );
  }

  if (selected) {
    return <StockDetail symbol={selected} onBack={() => setSelected(null)} accountId={accountId} />;
  }

  const cats = [
    { id: 'popular', label: 'Populares' },
    { id: 'etf', label: 'ETFs' },
    { id: 'movers', label: 'Más activas' },
    ...(analyzed.length ? [{ id: 'analizadas', label: 'Analizadas 🧠' }] : []),
  ];

  const getList = () => {
    switch (cat) {
      case 'popular': return POPULAR_SYMBOLS;
      case 'etf': return ETF_SYMBOLS;
      case 'movers': return movers;
      case 'analizadas': return analyzed;
      default: return POPULAR_SYMBOLS;
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-4 sm:py-6 pb-20 sm:pb-6">
      <div className="mb-4 sm:mb-6">
        <h1
          className="text-xl sm:text-2xl font-bold mb-1 select-none"
          onDoubleClick={() => { localStorage.removeItem('fp_investor_profile'); setInvestorProfile(null); setShowOnboarding(true); }}
        >Inversiones</h1>
        <p className="text-xs sm:text-sm text-[#6B7280]">Acciones, ETFs y opciones — desde $12 USD · comisión escalonada</p>
      </div>

      {/* Mini achievement banner */}
      <AchievementMini />

      {/* Portfolio recommendation banner */}
      {investorProfile && investorProfile.portfolio && PORTFOLIOS[investorProfile.portfolio] && (
        <div
          className="mb-4 sm:mb-6 p-4 rounded-xl border border-[#E5E7EB] bg-gradient-to-r from-[#F0F4FF] to-white cursor-pointer hover:shadow-sm transition"
          onClick={() => setMainTab('plan')}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white text-lg" style={{ backgroundColor: PORTFOLIOS[investorProfile.portfolio].color }}>
                {PORTFOLIOS[investorProfile.portfolio].icon || '📊'}
              </div>
              <div>
                <div className="text-sm font-bold text-[#181D27]">Portafolio {PORTFOLIOS[investorProfile.portfolio].name}</div>
                <div className="text-xs text-[#6B7280]">{PORTFOLIOS[investorProfile.portfolio].risk} · {PORTFOLIOS[investorProfile.portfolio].expectedReturn}</div>
              </div>
            </div>
            <div className="text-xs text-[#2A59C6] font-semibold">Ver plan ›</div>
          </div>
        </div>
      )}

      {/* Main tabs: Mercado / Portafolio / Mi Plan */}
      <div className="flex gap-0 border-b-2 border-[#E5E7EB] mb-4 sm:mb-6 overflow-x-auto">
        <button onClick={() => setMainTab('mercado')} className={`px-4 sm:px-6 py-2.5 sm:py-3 text-sm font-semibold border-b-2 -mb-0.5 whitespace-nowrap ${mainTab === 'mercado' ? 'text-[#2A59C6] border-[#2A59C6]' : 'text-[#6B7280] border-transparent hover:text-[#2A59C6]'}`}>
          Mercado
        </button>
        <button onClick={() => setMainTab('portafolio')} className={`px-4 sm:px-6 py-2.5 sm:py-3 text-sm font-semibold border-b-2 -mb-0.5 whitespace-nowrap ${mainTab === 'portafolio' ? 'text-[#2A59C6] border-[#2A59C6]' : 'text-[#6B7280] border-transparent hover:text-[#2A59C6]'}`}>
          Mi Portafolio
        </button>
        <button onClick={() => setMainTab('plan')} className={`px-4 sm:px-6 py-2.5 sm:py-3 text-sm font-semibold border-b-2 -mb-0.5 whitespace-nowrap ${mainTab === 'plan' ? 'text-[#2A59C6] border-[#2A59C6]' : 'text-[#6B7280] border-transparent hover:text-[#2A59C6]'}`}>
          Mi Plan
        </button>
        <button onClick={() => setMainTab('comparar')} className={`px-4 sm:px-6 py-2.5 sm:py-3 text-sm font-semibold border-b-2 -mb-0.5 whitespace-nowrap ${mainTab === 'comparar' ? 'text-[#2A59C6] border-[#2A59C6]' : 'text-[#6B7280] border-transparent hover:text-[#2A59C6]'}`}>
          Comparador
        </button>
      </div>

      {mainTab === 'comparar' && (
        <div className="max-w-4xl">
          <StockComparator />
        </div>
      )}

      {mainTab === 'plan' && (
        <div className="max-w-lg">
          {investorProfile && investorProfile.portfolio && PORTFOLIOS[investorProfile.portfolio] ? (() => {
            const p = PORTFOLIOS[investorProfile.portfolio];
            const composition = p.composition || p.allocation || [];
            return (
              <div>
                <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5 mb-4">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white text-xl" style={{ backgroundColor: p.color }}>
                      {p.icon || '📊'}
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-[#181D27]">Portafolio {p.name}</h3>
                      <p className="text-xs text-[#6B7280]">{p.desc}</p>
                    </div>
                  </div>
                  <div className="flex gap-4 mb-5">
                    <div className="flex-1 bg-[#F9FAFB] rounded-xl p-3 text-center">
                      <div className="text-xs text-[#6B7280]">Riesgo</div>
                      <div className="text-sm font-bold" style={{ color: p.color }}>{p.risk}</div>
                    </div>
                    <div className="flex-1 bg-[#F9FAFB] rounded-xl p-3 text-center">
                      <div className="text-xs text-[#6B7280]">Rendimiento</div>
                      <div className="text-sm font-bold text-[#181D27]">{p.expectedReturn}</div>
                    </div>
                  </div>
                  <h4 className="text-sm font-semibold text-[#181D27] mb-3">Distribución</h4>
                  <div className="space-y-2.5">
                    {composition.map((item) => {
                      // Enriquecer con info del ticker (nombre + qué hace).
                      // Si el ticker ya trae 'name' del PORTFOLIOS legacy, lo usa.
                      // Si no, busca en tickerInfo.js.
                      const info = getTickerInfo(item.symbol);
                      const displayName = item.name || info?.name || '';
                      const explanation = info?.what || '';
                      return (
                        <div key={item.symbol} className="flex items-center gap-3">
                          <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }} />
                          <div className="flex-1">
                            <div className="flex items-center justify-between gap-2">
                              <div className="min-w-0 flex-1">
                                <div className="flex items-baseline gap-1.5 flex-wrap">
                                  <span
                                    className="text-sm font-bold text-[#2A59C6] cursor-pointer hover:underline"
                                    onClick={() => setTickerPopover(item.symbol)}
                                  >
                                    {item.symbol}
                                  </span>
                                  {displayName && (
                                    <span className="text-xs text-[#6B7280]">· {displayName}</span>
                                  )}
                                </div>
                                {explanation && (
                                  <div className="text-[11px] text-[#9CA3AF] mt-0.5 leading-snug">{explanation}</div>
                                )}
                              </div>
                              <span className="text-sm font-semibold text-[#181D27] flex-shrink-0">{item.pct}%</span>
                            </div>
                            <div className="mt-1.5 h-1.5 bg-[#F3F4F6] rounded-full overflow-hidden">
                              <div className="h-full rounded-full" style={{ width: `${item.pct}%`, backgroundColor: item.color }} />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Audio narrado del portafolio asignado al usuario.
                    DECISION ESTRATEGICA May 24 2026: el audio aqui SIEMPRE es PREMIUM
                    sin importar el tier del usuario.
                    Razon: el portafolio es el producto de adquisicion/onboarding, no de
                    retencion. Cliente nuevo necesita la explicacion completa (~5min con
                    bear case + pre-mortem + companero mental) para tomar la decision de
                    invertir su primer peso. Tier gating aqui mata conversion.
                    Stocks SIGUEN con tier gating — ahi si tiene sentido (research progresivo). */}
                {(() => {
                  const audioUrl = getPortfolioAudioUrl(investorProfile.portfolio, 'premium');
                  return audioUrl ? (
                    <HeroAudioPlayer
                      src={audioUrl}
                      tier="premium"
                      labelHint={`Portafolio ${p.name}`}
                      kind="portfolio"
                    />
                  ) : null;
                })()}

                {/* Análisis de El Oráculo para el portafolio */}
                <PortfolioAnalysis />
                <button
                  onClick={() => setShowRoboAdvisor(true)}
                  className="w-full py-3.5 bg-[#2A59C6] text-white rounded-xl text-sm font-semibold hover:bg-[#1E4BB8] transition mb-3"
                >
                  Invertir en mi plan
                </button>
                <button
                  onClick={() => setShowOnboarding(true)}
                  className="w-full py-3 border border-[#E5E7EB] rounded-xl text-sm font-semibold text-[#6B7280] hover:bg-[#F9FAFB] transition"
                >
                  Repetir cuestionario
                </button>
              </div>
            );
          })() : (
            <div className="max-w-lg">
              {/* Hero — descubrimiento de qué te espera al completar el cuestionario */}
              <div className="rounded-2xl p-6 mb-4 text-white" style={{ background: 'linear-gradient(135deg, #0a2540 0%, #1a3a6c 100%)' }}>
                <div className="text-[10px] uppercase tracking-wider opacity-70 mb-2 font-semibold">
                  Tu portafolio personalizado te espera
                </div>
                <h3 className="text-xl font-bold mb-2 leading-snug">
                  En 7 preguntas te asignamos un plan de inversión
                </h3>
                <p className="text-sm opacity-90 leading-relaxed mb-4">
                  Tu plan viene con <strong>análisis institucional completo</strong> narrado por
                  el Oráculo — filosofía, apuestas centrales, comportamiento macro,
                  pre-mortem y bear case honesto. Audio de ~5 minutos para que
                  entiendas exactamente en qué estás invirtiendo antes de poner tu primer peso.
                </p>
                <button
                  onClick={() => setShowOnboarding(true)}
                  className="px-6 py-3 bg-white text-[#0a2540] rounded-xl text-sm font-bold hover:bg-white/90 transition"
                >
                  Comenzar cuestionario →
                </button>
              </div>

              {/* Beneficios listados como confianza */}
              <div className="bg-white rounded-2xl border border-[#E5E7EB] p-5">
                <div className="text-xs font-semibold text-[#181D27] mb-3">Lo que recibes al completarlo:</div>
                <ul className="space-y-2.5">
                  <li className="flex gap-2.5 text-xs text-[#4B5563]">
                    <span className="text-[#10B981] flex-shrink-0">✓</span>
                    <span>Composición sugerida con ETFs y acciones diversificadas</span>
                  </li>
                  <li className="flex gap-2.5 text-xs text-[#4B5563]">
                    <span className="text-[#10B981] flex-shrink-0">✓</span>
                    <span>Audio premium del Oráculo explicando tu portafolio</span>
                  </li>
                  <li className="flex gap-2.5 text-xs text-[#4B5563]">
                    <span className="text-[#10B981] flex-shrink-0">✓</span>
                    <span>Tesis personalizada según tu temperamento y plazo</span>
                  </li>
                  <li className="flex gap-2.5 text-xs text-[#4B5563]">
                    <span className="text-[#10B981] flex-shrink-0">✓</span>
                    <span>Acceso al glosario clickeable con definiciones simples</span>
                  </li>
                </ul>
              </div>
            </div>
          )}
        </div>
      )}

      {mainTab === 'portafolio' && (
        <Portfolio onSelectStock={(s) => { setSelected(s); setMainTab('mercado'); }} accountId={accountId} />
      )}

      {mainTab === 'mercado' && (
        <>
          <div className="flex gap-2 mb-5">
            <button onClick={() => setGeoFilter('internacional')} className={`flex-1 py-2.5 rounded-xl text-sm font-semibold border transition ${geoFilter === 'internacional' ? 'bg-[#2A59C6] text-white border-[#2A59C6]' : 'bg-white border-[#E5E7EB] text-[#6B7280] hover:border-[#2A59C6]'}`}>🌎 Internacional</button>
            <button onClick={() => setGeoFilter('colombia')} className={`flex-1 py-2.5 rounded-xl text-sm font-semibold border transition ${geoFilter === 'colombia' ? 'bg-[#FCD34D] text-[#0A2540] border-[#FCD34D]' : 'bg-white border-[#E5E7EB] text-[#6B7280] hover:border-[#FCD34D]'}`}>Colombia</button>
          </div>
          {geoFilter === 'colombia' ? (
            <ColombiaMarketPlaceholder />
          ) : (
          <>
          <NyseStatusBanner />
          <div className="relative mb-6">
            <input
              type="text"
              placeholder="Buscar acciones, ETFs..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onBlur={() => setTimeout(() => setSearch(''), 200)}
              className="w-full pl-12 pr-4 py-3.5 border border-[#E5E7EB] rounded-xl text-base outline-none focus:border-[#2A59C6] bg-white"
            />
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-lg text-[#9CA3AF]">&#x1F50D;</span>
            {searchResults.length > 0 && (
              <div className="absolute top-full left-0 right-0 bg-white border border-[#E5E7EB] rounded-xl mt-1 shadow-lg z-10 max-h-80 overflow-y-auto">
                {searchResults.map((s) => (
                  <div key={s} onMouseDown={() => { setSelected(s); setSearch(''); }} className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-[#FAFAFA]">
                    <span className="text-sm font-bold text-[#2A59C6] w-16">{s}</span>
                    <span className="text-sm text-[#6B7280] truncate">{STOCK_NAMES[s] || ''}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex gap-2 mb-7 flex-wrap">
            {cats.map((c) => (
              <button key={c.id} onClick={() => setCat(c.id)} className={`px-4.5 py-2 border rounded-full text-xs font-semibold ${cat === c.id ? 'bg-[#2A59C6] text-white border-[#2A59C6]' : 'border-[#E5E7EB] text-[#6B7280] bg-white hover:border-[#2A59C6] hover:text-[#2A59C6]'}`}>
                {c.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="text-center py-10 text-[#9CA3AF]">Cargando datos de mercado...</div>
          ) : (
            <div>
              <h2 className="text-lg font-bold mb-4">{cats.find((c) => c.id === cat)?.label}</h2>
              <StockList symbols={getList()} snapshots={snapshots} onSelect={setSelected} />
            </div>
          )}

          {/* News feed */}
          <div className="mt-8">
            <NewsFeed onSymbolClick={(s) => setSelected(s)} />
          </div>
          </>
          )}
        </>
      )}

      {/* Ticker popover — se muestra cuando el usuario hace click en un ticker
          de la composicion del portafolio. Audio explicativo + boton para ir
          al analisis completo si esta en el catalogo del Cerebro 2.0. */}
      {tickerPopover && (
        <TickerPopover
          symbol={tickerPopover}
          onClose={() => setTickerPopover(null)}
          onSeeFullAnalysis={(sym) => {
            setSelected(sym);
            setMainTab('mercado');
          }}
        />
      )}
    </div>
  );
}
